import { on } from '@ember/modifier'
import Component from '@glimmer/component'
import { classesForTheme } from 'teacher-ui-client/components/copy-classes/themes'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { t } from 'ember-intl'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { action } from '@ember/object'
import { TeacherSelect } from 'teacher-ui-client/components/base/select/teacher'
import { CopyClassesTable } from 'teacher-ui-client/components/copy-classes/table'
import { type CopyClassesPage } from 'teacher-ui-client/components/page/copy-classes'
import {
  type TeacherOptionTypes,
  AllTeachersOption,
  CurrentTeacherOption,
} from 'teacher-ui-client/components/base/select/teacher/options'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import { or } from 'ember-truth-helpers'
import { PanelHeadingIcon } from 'teacher-ui-client/components/base/panel-heading-icon'
import type Teacher from 'teacher-ui-client/models/teacher'
import type Owner from '@ember/owner'

interface Signature {
  Args: {
    isSubco?: boolean
    toSubscriptionType: SubscriptionType
    fetchSchoolClasses: CopyClassesPage['fetchSchoolClasses']
    updateTeacherId: (teacherId: TeacherOptionTypes | string) => void
    reset: () => void
  }
}

export class ClassesPanel extends Component<Signature> {
  @service store!: Store

  @queryParam('subscription-type/copy-classes') teacherId?: TeacherOptionTypes | string

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    if (this.args.isSubco) {
      void this.store.findAll<Teacher>('teacher')
    }
  }

  get themeKey() {
    return subscriptionTypeToThemeKey(this.args.toSubscriptionType)
  }

  get themeClasses() {
    return classesForTheme(this.themeKey)
  }

  get additionalTeacherOptions() {
    return [AllTeachersOption, CurrentTeacherOption]
  }

  get teacherOptions(): Teacher[] {
    return this.store.peekAll<Teacher>('teacher')
  }

  <template>
    <section class="rounded-md border shadow-md">
      <div class="flex items-center justify-between rounded-t-md px-4 py-3 {{this.themeClasses.background.mid}}">
        <h3 class="flex items-center gap-3">
          <PanelHeadingIcon @icon="phosphor-users-three-fill" @iconColorClass={{this.themeClasses.text}} />
          <span class="whitespace-nowrap">{{t "components.copyClasses.classesSection.heading"}}</span>
        </h3>
        <button
          {{on "click" @reset}}
          data-test-reset-class-selector
          type="button"
          class="text-dusty-black-400 font-semibold"
        >{{t "reset"}}</button>
      </div>
      <div data-test-class-selector-body>
        <div class="flex items-center justify-between p-4 {{this.themeClasses.background.light}}">
          {{#if @isSubco}}
            <label class="font-normal">
              {{t "components.copyClasses.classesSection.showClassesFor"}}
              <TeacherSelect
                class="max-w-xs truncate"
                @additionalOptions={{this.additionalTeacherOptions}}
                @teachers={{this.teacherOptions}}
                @teacherId={{or this.teacherId AllTeachersOption.id}}
                @updateTeacherId={{@updateTeacherId}}
                @themeKey={{this.themeKey}}
              />
            </label>
          {{else}}
            <div />
          {{/if}}
          <HideEmptyClasses />
        </div>
        <CopyClassesTable
          class="w-full"
          @toSubscriptionType={{@toSubscriptionType}}
          @fetchSchoolClasses={{@fetchSchoolClasses}}
        />
      </div>
    </section>
  </template>
}

class HideEmptyClasses extends Component<unknown> {
  @queryParam('subscription-type/copy-classes') hideEmptyClasses!: boolean

  @action
  toggleHideEmptyClasses() {
    this.hideEmptyClasses = !this.hideEmptyClasses
  }

  <template>
    <label data-test-hide-empty-classes-label class="font-normal">
      <input
        type="checkbox"
        id="hideEmptyClasses"
        name="hideEmptyClasses"
        {{on "change" this.toggleHideEmptyClasses}}
        checked={{this.hideEmptyClasses}}
      />
      {{t "components.copyClasses.classesSection.hideEmptyClasses"}}</label>
  </template>
}
